import { supabase } from './client';

export interface CoverLetter {
  id: string;
  user_id: string;
  content: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function getActiveCoverLetter(userId: string): Promise<CoverLetter | null> {
  const { data, error } = await supabase
    .from('cover_letters')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.error('Error fetching active cover letter:', error);
    throw error;
  }

  return data;
}

export async function getAllCoverLetters(userId: string): Promise<CoverLetter[]> {
  const { data, error } = await supabase
    .from('cover_letters')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching cover letters:', error);
    throw error;
  }

  return data || [];
}

export async function saveCoverLetter(
  userId: string,
  content: string,
  isActive: boolean = true
): Promise<CoverLetter> {
  const { data, error } = await supabase
    .from('cover_letters')
    .insert({
      user_id: userId,
      content,
      is_active: isActive,
    })
    .select()
    .single();

  if (error) {
    console.error('Error saving cover letter:', error);
    throw error;
  }

  return data;
}

export async function updateCoverLetter(
  id: string,
  content: string,
  isActive?: boolean
): Promise<CoverLetter> {
  const updateData: Partial<CoverLetter> = { content };
  if (isActive !== undefined) {
    updateData.is_active = isActive;
  }

  const { data, error } = await supabase
    .from('cover_letters')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating cover letter:', error);
    throw error;
  }

  return data;
}

export async function setActiveCoverLetter(id: string, userId: string): Promise<CoverLetter> {
  const { data, error } = await supabase
    .from('cover_letters')
    .update({ is_active: true })
    .eq('id', id)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error setting active cover letter:', error);
    throw error;
  }

  return data;
}

export async function deleteCoverLetter(id: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('cover_letters')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);

  if (error) {
    console.error('Error deleting cover letter:', error);
    throw error;
  }
}

export async function saveOrUpdateActiveCoverLetter(
  userId: string,
  content: string
): Promise<CoverLetter> {
  const activeCoverLetter = await getActiveCoverLetter(userId);

  if (activeCoverLetter) {
    return await updateCoverLetter(activeCoverLetter.id, content, true);
  } else {
    return await saveCoverLetter(userId, content, true);
  }
}
